import React, { useState } from "react";
import { useTransition, animated, to, useSpring } from "react-spring";
import styled from "styled-components";

import { colors } from "../constants/theme";
import { useSchoolSettings } from "../hooks/useSchoolSettings";
import CourseModal from "./CourseModal";
import { Row, Col } from "./LayoutBlocks";

const Wrapper = styled.section`
  position: relative;
  width: 100%;
  z-index: 1;
`;

const CELL_HEIGHT = 60;

const CourseCell = styled(animated.button)`
  padding: 0;
  cursor: pointer;
  outline: none;
  border: none !important;
  background: transparent !important;
  position: absolute;
  will-change: transform, height, opacity;
  height: ${CELL_HEIGHT}px;
  padding-bottom: 4px;
  width: 100%;
  text-decoration: none;
  color: #fff;
`;

const CellContent = styled.div`
  text-align: left;
  position: relative;
  width: 100%;
  padding-top: 1.5em;

  p {
    position: absolute;
    left: 0;
    top: 0;
    color: #000;
    margin: 0;
    margin-bottom: 3px;
    white-space: nowrap;
  }
  p.last-column {
    transform: translateX(-100%);
    left: 100%;
  }

  transition: transform 250ms ease;

  &:hover {
    opacity: 0.8;
  }
`;

const CellBlock = styled.div`
  background-color: ${({ isSelective }) =>
    isSelective ? colors.tintColor2 : colors.tintColor};
  width: 100%;
  height: 30px;
`;

const getWidthAndX = (course) => {
  const { timer_1g, timer_2g, timer_3g } = course;
  const asBools = [timer_1g > 0, timer_2g > 0, timer_3g > 0];
  const yearCounts = asBools.filter(Boolean).length;
  const x = (asBools.findIndex(Boolean) * 1) / 3;
  const width = yearCounts / 3;
  const isLastCol = x > 0.66;
  return { x, width, isLastCol };
};

const AnimatedContent = ({ selectedCourses, header, onClickCourse }) => {
  let height = header && window.screen.width <= 420 ? 80 : 0;

  const transition = useTransition(
    selectedCourses.map((data) => ({
      ...data,
      ...getWidthAndX(data),
      height: CELL_HEIGHT,
      y: (height += CELL_HEIGHT) - CELL_HEIGHT,
    })),

    {
      keys: (d) => d.fag.ID,
      from: ({ x, y, width }) => ({
        width,
        opacity: 0,
        scale: 0.9,
        x,
        y: y - 50,
      }),
      leave: ({ x, y, width }) => ({
        width,
        opacity: 0,
        scale: 0.9,
        x,
        y: y - 50,
      }),
      enter: ({ x, y, width }) => ({ x, y, width, scale: 1, opacity: 1 }),
      update: ({ x, y, width }) => ({ x, y, width }),
      trail: 25,
    }
  );

  if (!selectedCourses.length) {
    return null;
  }

  return (
    <Wrapper style={{ height }} className="studySelector_table-wrapper">
      {header && <Header>{header}</Header>}
      {transition((style, item, { key }, index) => {
        const { y, x, width, scale, ...rest } = style;

        if (!item) {
          return null;
        }

        const classes = ["studySelector_course-cell"];

        if (item.isSelective) {
          classes.push("studySelector_optional-subject");
        } else {
          classes.push("studySelector_mandatory-subject");
        }

        return (
          <>
            <CourseCell
              key={key}
              className={classes.join(" ")}
              onClick={() => onClickCourse(item)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                zIndex: selectedCourses.length - index,
                transform: to(
                  [y.to((v) => v), scale.to((v) => v)],
                  (y, scale) => `translate3d(5px,${y}px, 0) scale(${scale})`
                ),
                left: x.to((v) => v * 100 + "%"),
                width: width.to((v) => `calc(${v * 100}% - 10px)`),
                ...rest,
              }}
            >
              <CellContent
                isSelective={item.isSelective}
                className="studySelector_course-cell-content"
              >
                <p className={item.isLastCol ? "last-column" : ""}>
                  {item?.fag?.post_title}
                </p>
                <CellBlock isSelective={item.isSelective} />
              </CellContent>
            </CourseCell>
          </>
        );
      })}
    </Wrapper>
  );
};

const Header = styled.h2`
  position: absolute;
  top: 1em;
  left: 0;
  right: 0;
  border-bottom: 2px solid #111;
  margin: 0 5px;
  @media only screen and (min-width: 421px) {
    display: none;
  }
`;

const CoursesTable = (props) => {
  const { timer, selectedCourses } = props;

  const aTimer = useSpring({ 1: timer[0], 2: timer[1], 3: timer[2] });

  const { showHours } = useSchoolSettings();
  const [modalCourse, setModalCourse] = useState();

  return (
    <TableSection className="studySelector_table-section">
      <BackgroundColumns className="studySelector_background-columns">
        <span className="studySelector_bg-column studySelector_bg-column-1g" />
        <span className="studySelector_bg-column studySelector_bg-column-2g" />
        <span className="studySelector_bg-column studySelector_bg-column-3g" />
      </BackgroundColumns>
      <Row between className="studySelector_result-header-row">
        <Col center className="studySelector_result-header">
          <h3>
            1. g
            {showHours && (
              <>
                {" "}
                (
                <animated.span>
                  {aTimer[1].to((x) => x.toFixed(0))}
                </animated.span>{" "}
                timer)
              </>
            )}
          </h3>
        </Col>
        <Col center className="studySelector_result-header">
          <h3>
            2. g
            {showHours && (
              <>
                {" "}
                (
                <animated.span>
                  {aTimer[2].to((x) => x.toFixed(0))}
                </animated.span>{" "}
                timer)
              </>
            )}
          </h3>
        </Col>
        <Col center className="studySelector_result-header">
          <h3>
            3. g
            {showHours && (
              <>
                {" "}
                (
                <animated.span>
                  {aTimer[3].to((x) => x.toFixed(0))}
                </animated.span>{" "}
                timer)
              </>
            )}
          </h3>
        </Col>
      </Row>

      <div style={{ display: "flex", width: "100%" }}>
        <CourseTypes selectedCourses={selectedCourses} />

        <div style={{ width: "100%" }}>
          <AnimatedContent
            header="Obligatoriske fag"
            selectedCourses={selectedCourses.filter(
              (c) => !c.isSelective && !c.studieretningfag
            )}
            onClickCourse={setModalCourse}
          />
          <AnimatedContent
            header="Studieretningsfag"
            selectedCourses={selectedCourses.filter((c) => c.studieretningfag)}
            onClickCourse={setModalCourse}
          />
          <AnimatedContent
            header="Valgfag"
            selectedCourses={selectedCourses.filter((c) => c.isSelective)}
            onClickCourse={setModalCourse}
          />
        </div>
      </div>

      {modalCourse && (
        <CourseModal
          {...modalCourse}
          closeCourse={() => setModalCourse(null)}
        />
      )}
    </TableSection>
  );
};

const TableSection = styled.div`
  position: relative;
  z-index: 0;
`;

const BackgroundColumns = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  flex-direction: row;
  display: flex;
  z-index: -1;
  > * {
    height: 100%;
    width: 33.333%;
    display: block;
  }
`;

const HorizontalTextBlock = styled.div`
  writing-mode: tb-rl;
  transform: rotate(180deg);
  width: 40px;
  text-align: center;
  position: relative;

  :after {
    content: "";
    width: 2px;
    height: calc(100% - 16px);
    background-color: #111;
    position: absolute;
    top: 8px;
    left: 0;
  }

  p {
    line-height: 40px;
    margin: 0;
  }
`;

const CoursesLabelsContainer = styled.div`
  display: flex;
  flex-direction: column;
  @media only screen and (max-width: 420px) {
    display: none;
  }
`;

const CourseTypes = ({ selectedCourses }) => {
  const studieretningfag = selectedCourses.filter(
    (c) => c.studieretningfag
  ).length;
  const valgfag = selectedCourses.filter((c) => c.isSelective).length;
  const obligatoriske = selectedCourses.length - valgfag - studieretningfag;

  return (
    <CoursesLabelsContainer className="studySelector_course-type-labels">
      {!!obligatoriske && (
        <HorizontalTextBlock
          style={{ height: obligatoriske * CELL_HEIGHT }}
          className="studySelector_obligatoriske-label"
        >
          <p>Obligatoriske fag</p>
        </HorizontalTextBlock>
      )}
      {!!studieretningfag && (
        <HorizontalTextBlock
          className="studySelector_studieretning-label"
          style={{ height: studieretningfag * CELL_HEIGHT }}
        >
          <p>Studieretningsfag</p>
        </HorizontalTextBlock>
      )}
      {!!valgfag && (
        <HorizontalTextBlock
          className="studySelector_valgfag-label"
          style={{ height: valgfag * CELL_HEIGHT }}
        >
          <p>Valgfag</p>
        </HorizontalTextBlock>
      )}
    </CoursesLabelsContainer>
  );
};

export default CoursesTable;
